import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { ApiResponse } from '@/types'
import type { ParkingZone } from '@/types/parking'

// Query keys
export const parkingZoneKeys = {
  all: ['parking_zones'] as const,
  lists: () => [...parkingZoneKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...parkingZoneKeys.lists(), filters] as const,
  details: () => [...parkingZoneKeys.all, 'detail'] as const,
  detail: (id: number) => [...parkingZoneKeys.details(), id] as const,
}

// Fetch zones for a property
export function useZones(propertyId: number) {
  return useQuery({
    queryKey: parkingZoneKeys.list({ propertyId }),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<ParkingZone[]>>(
        `/api/properties/${propertyId}/parking_zones`
      )
      return data
    },
    enabled: !!propertyId,
  })
}

// Fetch single zone
export function useZone(id: number) {
  return useQuery({
    queryKey: parkingZoneKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<ParkingZone>>(`/api/parking_zones/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Create zone
export interface CreateZoneInput {
  property_id: number
  name: string
  zone_type: 'car' | 'motorcycle' | 'mixed'
  total_slots: number
  notes?: string
}

export function useCreateZone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ property_id, ...rest }: CreateZoneInput) => {
      const { data } = await api.post<ApiResponse<ParkingZone>>(
        `/api/properties/${property_id}/parking_zones`,
        { parking_zone: rest }
      )
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, parkingZoneKeys.lists())
    },
  })
}

// Update zone
export interface UpdateZoneInput {
  id: number
  name?: string
  zone_type?: 'car' | 'motorcycle' | 'mixed'
  total_slots?: number
  notes?: string
}

export function useUpdateZone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateZoneInput) => {
      const { data } = await api.patch<ApiResponse<ParkingZone>>(`/api/parking_zones/${id}`, {
        parking_zone: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, parkingZoneKeys.lists())
      invalidateDetailQueries(queryClient, parkingZoneKeys.detail(data.id))
    },
  })
}

// Generate / rotate the public self-check-in key for a zone (admin-only; the
// backend authorizes via Pundit `rotate_public_key?`). Returns the new key and
// the assembled public URL. Invalidates the zone detail so the sticker refreshes.
export interface RotatePublicKeyResult {
  id: number
  public_check_in_key: string
  public_check_in_url: string
}

export function useRotatePublicKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ApiResponse<RotatePublicKeyResult>>(
        `/api/parking_zones/${id}/rotate_public_key`
      )
      return data.data
    },
    onSuccess: (data) => {
      invalidateDetailQueries(queryClient, parkingZoneKeys.detail(data.id))
    },
  })
}

// Delete zone
export function useDeleteZone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/parking_zones/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, parkingZoneKeys.lists())
    },
  })
}

// EP-105 — write the per-zone session-fee formula (admin-only). Invalidates the
// zone detail so the sticker card / fee table refreshes after save. The formula
// comes through as the canonical ParkingFeeLine union (daily / per_vehicle_type
// / hourly tier).
export interface UpdateSessionFeeInput {
  id: number
  formula: ParkingZone['session_fee_formula']
}

export interface UpdateSessionFeeError {
  code: string
  message: string
}

export function useUpdateSessionFee() {
  const queryClient = useQueryClient()

  return useMutation<ParkingZone, { response: { data: { error: UpdateSessionFeeError } } }, UpdateSessionFeeInput>({
    mutationFn: async ({ id, formula }) => {
      const { data } = await api.put<ApiResponse<ParkingZone>>(
        `/api/parking_zones/${id}/session_fee`,
        { parking_zone: { session_fee_formula: formula } }
      )
      return data.data
    },
    onSuccess: (zone) => {
      invalidateDetailQueries(queryClient, parkingZoneKeys.detail(zone.id))
    },
  })
}
